// services/errorHandlingService.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define error types for better categorization
export enum ErrorType {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  SERVER = 'server',
  CLIENT = 'client',
  UNKNOWN = 'unknown'
}

export interface ErrorDetails {
  id: string;
  type: ErrorType;
  message: string;
  timestamp: number;
  source?: string;
  statusCode?: number;
  data?: any;
  handled: boolean;
  // RFC 9457 fields
  title?: string;
  detail?: string;
  typeUri?: string;
  instance?: string;
}

// RFC 9457 Problem Details structure
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  [key: string]: any;
}

// State interface for the error slice
interface ErrorState {
  errors: ErrorDetails[];
  globalError: ErrorDetails | null;
  isErrorModalOpen: boolean;
}

// Initial state
const initialState: ErrorState = {
  errors: [],
  globalError: null,
  isErrorModalOpen: false
};

// Error translations map
const ERROR_TRANSLATIONS: Record<string, { title: string; message: string }> = {
  'https://api.plazofijo.com/errors/internal-error': {
    title: 'Error Interno del Servidor',
    message: 'Se ha producido un error interno en el servidor. Por favor, contacte al soporte técnico si el problema persiste.'
  },
  'https://api.plazofijo.com/errors/validation-error': {
    title: 'Error de Validación',
    message: 'Los datos enviados no son válidos. Por favor, verifique la información.'
  },
  'https://api.plazofijo.com/errors/unauthorized': {
    title: 'No Autorizado',
    message: 'No tiene permisos para realizar esta acción.'
  },
  // Add more translations as needed
};

const getTranslation = (typeUri: string | undefined): { title?: string; message?: string } | null => {
  if (!typeUri || !ERROR_TRANSLATIONS[typeUri]) return null;
  return ERROR_TRANSLATIONS[typeUri];
};

// Create Redux slice for error handling
const errorSlice = createSlice({
  name: 'error',
  initialState,
  reducers: {
    addError: (state, action: PayloadAction<Omit<ErrorDetails, 'id' | 'timestamp' | 'handled'>>) => {
      const newError: ErrorDetails = {
        ...action.payload,
        id: Date.now().toString(),
        timestamp: Date.now(),
        handled: false
      };

      state.errors.push(newError);

      // Set as global error if it's a critical error type or specific server errors
      if (
        action.payload.type === ErrorType.NETWORK ||
        action.payload.type === ErrorType.SERVER ||
        action.payload.type === ErrorType.AUTHENTICATION ||
        action.payload.type === ErrorType.VALIDATION ||
        action.payload.type === ErrorType.AUTHORIZATION ||
        action.payload.type === ErrorType.UNKNOWN // Also show unknown errors globally to be safe
      ) {
        state.globalError = newError;
        state.isErrorModalOpen = true;
      }
    },

    markErrorAsHandled: (state, action: PayloadAction<string>) => {
      const errorIndex = state.errors.findIndex(e => e.id === action.payload);
      if (errorIndex !== -1) {
        state.errors[errorIndex].handled = true;
      }

      // Clear global error if it was marked as handled
      if (state.globalError?.id === action.payload) {
        state.globalError = null;
        state.isErrorModalOpen = false;
      }
    },

    clearErrors: (state) => {
      state.errors = [];
      state.globalError = null;
      state.isErrorModalOpen = false;
    },

    closeErrorModal: (state) => {
      state.isErrorModalOpen = false;
    }
  }
});

export const { addError, markErrorAsHandled, clearErrors, closeErrorModal } = errorSlice.actions;

export default errorSlice.reducer;

// Utility functions for error handling

/**
 * Maps HTTP status codes to error types
 */
export const mapStatusToErrorType = (status: number): ErrorType => {
  if (status >= 500) return ErrorType.SERVER;
  if (status === 401) return ErrorType.AUTHENTICATION;
  if (status === 403) return ErrorType.AUTHORIZATION;
  if (status === 400 || status === 422) return ErrorType.VALIDATION;
  if (status === 0 || status === 408 || status === 504) return ErrorType.NETWORK;
  return ErrorType.UNKNOWN;
};

/**
 * Creates an error object from an API error response
 */
export const createApiError = (error: any): Omit<ErrorDetails, 'id' | 'timestamp' | 'handled'> => {
  // Handle Axios or fetch error
  const status = error.response?.status || error.status || 0;
  const errorType = mapStatusToErrorType(status);
  const errorData = error.response?.data || error.data || error;

  let message = 'Ocurrió un error inesperado';
  let title = 'Error';
  let detail: string | undefined;
  let typeUri: string | undefined;
  let instance: string | undefined;

  // Check for RFC 9457 Problem Details
  if (errorData && typeof errorData === 'object' && 'type' in errorData) {
    const problem = errorData as ProblemDetails;
    typeUri = problem.type;
    title = problem.title || title;
    detail = problem.detail;
    instance = problem.instance;

    // Use translation if available
    const translation = getTranslation(typeUri);
    if (translation) {
      title = translation.title || title;
      // We append the detail to the friendly message if it exists, or use the detail as fallback
      if (problem.detail) {
         // Keep the friendly message as primary, but maybe expose detail in a specialized field
         message = translation.message || problem.detail;
      } else {
         message = translation.message || message;
      }
    } else {
      // Fallback to title/detail from response if no translation
      if (problem.detail) {
        message = problem.detail;
      } else if (problem.title) {
        message = problem.title;
      }
    }
  } else {
    // Legacy error handling
    switch (errorType) {
      case ErrorType.NETWORK:
        message = 'Error de conexión. Verifique su red e intente nuevamente.';
        title = 'Error de Conexión';
        break;
      case ErrorType.AUTHENTICATION:
        message = 'Su sesión ha expirado. Por favor, inicie sesión nuevamente.';
        title = 'Sesión Expirada';
        break;
      case ErrorType.AUTHORIZATION:
        message = 'No tiene permisos para realizar esta acción.';
        title = 'Acceso Denegado';
        break;
      case ErrorType.VALIDATION:
        message = errorData?.message || 'Los datos proporcionados no son válidos.';
        title = 'Datos Inválidos';
        break;
      case ErrorType.SERVER:
        message = 'Error en el servidor. Por favor, intente más tarde.';
        title = 'Error del Servidor';
        break;
      default:
        break;
    }
  }

  return {
    type: errorType,
    message,
    title,
    detail,
    typeUri,
    instance,
    statusCode: status,
    source: error.config?.url || error.url || 'client',
    data: errorData
  };
};

/**
 * Error handler to be used with API requests
 */
export const handleApiError = (error: any, dispatch: any) => {
  const errorDetails = createApiError(error);
  dispatch(addError(errorDetails));
  return errorDetails;
};

/**
 * Utility to create a custom error
 */
export const createCustomError = (
  message: string,
  type: ErrorType = ErrorType.CLIENT,
  data?: any
): Omit<ErrorDetails, 'id' | 'timestamp' | 'handled'> => {
  return {
    type,
    message,
    source: 'client',
    data
  };
};

/**
 * Wraps API calls with error handling
 */
export const withErrorHandling = async <T>(
  apiCall: () => Promise<T>,
  dispatch: any,
  customErrorHandler?: (error: any) => void
): Promise<T> => {
  try {
    return await apiCall();
  } catch (error: any) {
    const errorDetails = handleApiError(error, dispatch);

    if (customErrorHandler) {
      customErrorHandler(errorDetails);
    }

    throw error; // Re-throw to allow caller to handle if needed
  }
};